/**
 * BattleArena — live 1v1 (or 3-way) battle UI
 *
 * Shows both players opening their packs simultaneously.
 * Cards stay face-down until the backend resolves and broadcasts the result.
 * Then both flip at the same moment for maximum drama.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatUnits } from "viem";
import { usePublicClient } from "wagmi";
import confetti from "canvas-confetti";
import { drawPackCards }   from "../hooks/usePackDraw";
import { PokemonCard }     from "./PokemonCard";
import { TIER_DATA, TOKEN_SYMBOLS } from "./PackSelector";
import { CARD_PACK_BATTLE_ABI, CARD_PACK_BATTLE_ADDRESS } from "../CardPackBattleABI";
import { helperToast }     from "lib/helperToast";
import "./BattleArena.scss";

// ─── Types ───────────────────────────────────────────────────────────────────
export interface BattleData {
  battleId:    string;
  tier:        number;
  payToken:    number;
  mode:        number;
  maxSlots:    number;
  entryAmount: string;
  mySlot:      number;   // -1 = spectator
  myAddress:   string;
  players:     string[];
  seeds:       string[];
  status:      "open" | "active" | "resolved" | "cancelled";
}

interface BattleSlotState {
  address:      string;
  seed:         string;
  card:         ReturnType<typeof drawPackCards>[0] | null;
  cardValueUsd: number;
  revealed:     boolean;
  isWinner:     boolean;
}

interface BattleArenaProps {
  battle:    BattleData;
  account:   string | undefined;
  onClose:   () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_INDEXER_API ?? "";
const WS_BASE  = API_BASE.replace(/^http/, "ws");

function shortAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtToken(val: string | bigint, token: number): string {
  const n = Number(formatUnits(BigInt(val.toString()), 18));
  const sym = TOKEN_SYMBOLS[token as 0|1|2|3|4] ?? "?";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${sym}`;
}

type ArenaPhase =
  | "waiting"      // waiting for all players to join
  | "countdown"    // 3 2 1 GO!
  | "opening"      // pack shake animation
  | "revealed"     // cards flipped, winner shown
  | "timeout"      // battle timed out
  | "cancelled";   // battle cancelled

// ─── SlotPanel — one player's side ───────────────────────────────────────────
function SlotPanel({ slot, isMe, phase, tier, totalSlots }: {
  slot:       BattleSlotState | null;
  isMe:       boolean;
  phase:      ArenaPhase;
  tier:       number;
  totalSlots: number;
}) {
  const tierData = TIER_DATA[tier];

  return (
    <div className={`BA__slot${isMe ? " BA__slot--me" : ""}${slot?.isWinner ? " BA__slot--winner" : ""}${slot?.revealed && !slot.isWinner ? " BA__slot--loser" : ""}`}>
      {/* Player label */}
      <div className="BA__slot-label">
        {isMe ? <span className="BA__you-badge">YOU</span> : null}
        <span className="BA__player-addr">
          {slot ? shortAddr(slot.address) : "Waiting…"}
        </span>
      </div>

      {/* Card area */}
      <div className="BA__card-wrap">
        <AnimatePresence mode="wait">
          {!slot || !slot.revealed ? (
            // Face-down card back
            <motion.div key="back" className="BA__card-back"
              animate={phase === "opening" ? {
                x: [0, -8, 8, -6, 6, 0],
                rotate: [0, -3, 3, -2, 2, 0],
              } : {}}
              transition={{ duration: 0.6, repeat: phase === "opening" ? Infinity : 0, repeatDelay: 0.2 }}>
              <div className="BA__card-back-inner">
                <span className="BA__card-back-icon">🃏</span>
                <span className="BA__card-back-tier">{tierData?.label ?? "?"}</span>
              </div>
            </motion.div>
          ) : (
            // Revealed card
            <motion.div key="front"
              initial={{ rotateY: 90, scale: 0.9 }}
              animate={{ rotateY: 0, scale: 1 }}
              transition={{ duration: 0.5, type: "spring", stiffness: 200 }}>
              {slot.card ? (
                <PokemonCard card={slot.card} size="lg" />
              ) : (
                <div className="BA__card-placeholder">
                  <span>?</span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Value display */}
      <AnimatePresence>
        {slot?.revealed && (
          <motion.div className="BA__slot-value"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}>
            <span className="BA__value-usd">
              ${slot.cardValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
            {slot.card && (
              <span className="BA__card-name">{slot.card.name}</span>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Winner / Loser badge */}
      <AnimatePresence>
        {slot?.revealed && (
          <motion.div
            className={`BA__result-badge ${slot.isWinner ? "BA__result-badge--win" : "BA__result-badge--lose"}`}
            initial={{ opacity: 0, scale: 0.6, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 300 }}>
            {slot.isWinner ? "🏆 Winner!" : "💀 Lost"}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── VS separator ─────────────────────────────────────────────────────────────
function VsBadge({ phase, countdown }: { phase: ArenaPhase; countdown: number }) {
  return (
    <div className="BA__vs">
      <AnimatePresence mode="wait">
        {phase === "countdown" && countdown > 0 ? (
          <motion.div key={`c${countdown}`} className="BA__countdown"
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.3 }}>
            {countdown}
          </motion.div>
        ) : phase === "countdown" ? (
          <motion.div key="go" className="BA__go"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1.3, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}>
            GO!
          </motion.div>
        ) : (
          <motion.div key="vs" className="BA__vs-text"
            animate={phase === "opening" ? {
              scale: [1, 1.1, 1],
              textShadow: ["0 0 10px rgba(139,92,246,0.3)", "0 0 30px rgba(139,92,246,0.8)", "0 0 10px rgba(139,92,246,0.3)"],
            } : {}}
            transition={{ duration: 1, repeat: phase === "opening" ? Infinity : 0 }}>
            VS
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main BattleArena ─────────────────────────────────────────────────────────
export function BattleArena({ battle, account, onClose }: BattleArenaProps) {
  const [phase,     setPhase]     = useState<ArenaPhase>(
    battle.status === "active" ? "countdown" : "waiting"
  );
  const [countdown, setCountdown] = useState(3);
  const [slots,     setSlots]     = useState<(BattleSlotState | null)[]>(
    Array.from({ length: battle.maxSlots }, () => null)
  );
  const [prize,     setPrize]     = useState<string>("");
  const [mode,      setMode]      = useState(battle.mode);

  const wsRef       = useRef<WebSocket | null>(null);
  const sigPostedRef = useRef(false);
  const publicClient = usePublicClient();

  const isSpectator = battle.mySlot < 0;

  // ── Fetch full battle state from contract to populate seeds ───────────────
  useEffect(() => {
    if (!publicClient) return;
    (async () => {
      try {
        const data = await publicClient.readContract({
          address:      CARD_PACK_BATTLE_ADDRESS,
          abi:          CARD_PACK_BATTLE_ABI,
          functionName: "getBattle",
          args:         [BigInt(battle.battleId)],
        }) as any;

        const players: string[] = data.players ?? battle.players;
        const seeds:   string[] = (data.seeds ?? []).map((s: bigint) => s.toString());

        setSlots(prev => prev.map((_, i) => {
          if (!players[i]) return null;
          const seedStr = seeds[i] ?? "0";
          const cards = seedStr !== "0"
            ? drawPackCards(BigInt(seedStr), battle.tier, false)
            : [];
          return {
            address:      players[i].toLowerCase(),
            seed:         seedStr,
            card:         cards[0] ?? null,
            cardValueUsd: cards[0]?.approxValueUsd ?? 0,
            revealed:     false,
            isWinner:     false,
          };
        }));
      } catch {}
    })();
  }, [publicClient, battle.battleId, battle.tier, battle.players]);

  // ── Countdown → opening ───────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "countdown") return;
    const t = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(t);
          setPhase("opening");
          return 0;
        }
        return c - 1;
      });
    }, 900);
    return () => clearInterval(t);
  }, [phase]);

  // ── After "opening" starts, submit our card value to backend ─────────────
  useEffect(() => {
    if (phase !== "opening" || isSpectator || sigPostedRef.current) return;
    sigPostedRef.current = true;

    const mySlot = battle.mySlot;
    const mySlotData = slots[mySlot];
    if (!mySlotData) return;

    const cardValueUsd18 = BigInt(
      Math.round((mySlotData.cardValueUsd || 1) * 1e18)
    ).toString();

    fetch(`${API_BASE}/api/battles/${battle.battleId}/sign-slot`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        slotIndex:       mySlot,
        cardValueUsd18,
        playerAddress:   account,
      }),
    }).catch(() => {});
  }, [phase, isSpectator, battle.mySlot, battle.battleId, slots, account]);

  // ── WebSocket for real-time battle events ─────────────────────────────────
  useEffect(() => {
    if (!WS_BASE) return;
    const ws = new WebSocket(`${WS_BASE}/ws/battles`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);

        if (msg.type === "battle_slot_filled" && msg.data.battleId === battle.battleId) {
          const { slotIndex, player, seed } = msg.data;
          setSlots(prev => {
            const next = [...prev];
            const cards = seed && seed !== "0"
              ? drawPackCards(BigInt(seed), battle.tier, false)
              : [];
            next[slotIndex] = {
              address:      player.toLowerCase(),
              seed,
              card:         cards[0] ?? null,
              cardValueUsd: cards[0]?.approxValueUsd ?? 0,
              revealed:     false,
              isWinner:     false,
            };
            return next;
          });
          // If all slots now filled, trigger countdown
          setSlots(prev => {
            const allFilled = prev.every(s => s !== null);
            if (allFilled) setTimeout(() => setPhase("countdown"), 500);
            return prev;
          });
        }

        if (msg.type === "battle_active" && msg.data.battleId === battle.battleId) {
          setPhase("countdown");
        }

        if (msg.type === "battle_resolved" && msg.data.battleId === battle.battleId) {
          const { winner, cardValues, seeds, prizeAmount } = msg.data;
          const winnerAddr = winner.toLowerCase();
          setPrize(fmtToken(prizeAmount, battle.payToken));

          // Update slots with final revealed data
          setSlots(prev => prev.map((s, i) => {
            if (!s) return s;
            const seedToUse = seeds[i] ?? s.seed;
            const cards = seedToUse && seedToUse !== "0"
              ? drawPackCards(BigInt(seedToUse), battle.tier, false)
              : [];
            const valRaw = cardValues[i] ? Number(cardValues[i]) / 1e18 : s.cardValueUsd;
            return {
              ...s,
              seed:         seedToUse,
              card:         cards[0] ?? s.card,
              cardValueUsd: valRaw,
              revealed:     true,
              isWinner:     s.address.toLowerCase() === winnerAddr,
            };
          }));

          // Reveal animation timing
          setTimeout(() => {
            setPhase("revealed");
            // Confetti for local player if winner
            const mySlotData = slots[battle.mySlot];
            if (mySlotData && mySlotData.address.toLowerCase() === winnerAddr) {
              confetti({ particleCount: 160, spread: 80, origin: { y: 0.5 }, colors: ["#fbbf24","#a78bfa","#34d399","#fff"] });
            }
          }, 800);
        }

        if (msg.type === "battle_cancelled" && msg.data.battleId === battle.battleId) {
          setPhase("cancelled");
        }
      } catch {}
    };

    ws.onerror = () => {};
    return () => { ws.close(); };
  }, [battle.battleId, battle.payToken, battle.tier, battle.mySlot, slots]);

  // ── Waiting → countdown when all slots filled ─────────────────────────────
  useEffect(() => {
    if (phase !== "waiting") return;
    const allFilled = slots.every(s => s !== null) && slots.length === battle.maxSlots;
    if (allFilled) {
      setTimeout(() => setPhase("countdown"), 300);
    }
  }, [slots, phase, battle.maxSlots]);

  const mySlotData = battle.mySlot >= 0 ? slots[battle.mySlot] : null;
  const amWinner   = mySlotData?.isWinner ?? false;

  return (
    <motion.div className="BA"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

      {/* Ambient glow */}
      <div className={`BA__ambient${phase === "revealed" ? (amWinner ? " BA__ambient--win" : " BA__ambient--lose") : ""}`} />

      {/* Top bar */}
      <div className="BA__topbar">
        <div className="BA__battle-info">
          <span className="BA__battle-tier">{TIER_DATA[battle.tier]?.label} Pack</span>
          <span className="BA__battle-mode">{battle.mode === 0 ? "⚔️ Standard" : "👾 Underdog"}</span>
        </div>
        <div className="BA__battle-prize">
          {prize ? (
            <>Prize <strong>{prize}</strong></>
          ) : (
            <>Entry {fmtToken(battle.entryAmount, battle.payToken)} each</>
          )}
        </div>
        <button className="BA__close" onClick={onClose}>✕ Exit</button>
      </div>

      {/* Phase status */}
      <AnimatePresence>
        {phase === "waiting" && (
          <motion.div className="BA__status-bar" key="waiting"
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="BA__status-dot" />
            Waiting for all players to join…
          </motion.div>
        )}
        {phase === "opening" && (
          <motion.div className="BA__status-bar BA__status-bar--live" key="opening"
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="BA__status-dot BA__status-dot--live" />
            ⚔️ Packs being opened — awaiting reveal…
          </motion.div>
        )}
        {phase === "revealed" && (
          <motion.div
            className={`BA__result-banner ${amWinner ? "BA__result-banner--win" : isSpectator ? "BA__result-banner--spectate" : "BA__result-banner--lose"}`}
            key="result"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 250, delay: 0.5 }}>
            {isSpectator
              ? `🏆 ${shortAddr(slots.find(s => s?.isWinner)?.address ?? "")} wins ${prize}!`
              : amWinner
                ? `🏆 You Win! +${prize}`
                : `💀 Better luck next time`}
          </motion.div>
        )}
        {phase === "cancelled" && (
          <motion.div className="BA__result-banner BA__result-banner--cancel" key="cancel"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            ⏳ Battle cancelled — funds refunded
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main arena */}
      <div className={`BA__arena BA__arena--${battle.maxSlots}p`}>
        {Array.from({ length: battle.maxSlots }).map((_, i) => (
          <SlotPanel
            key={i}
            slot={slots[i]}
            isMe={!isSpectator && i === battle.mySlot}
            phase={phase}
            tier={battle.tier}
            totalSlots={battle.maxSlots}
          />
        ))}

        {/* VS badge (only for 1v1) */}
        {battle.maxSlots === 2 && (
          <VsBadge phase={phase} countdown={countdown} />
        )}
      </div>

      {/* Spectator badge */}
      {isSpectator && phase !== "revealed" && (
        <div className="BA__spectator-badge">👁 Spectating</div>
      )}

      {/* Post-battle actions */}
      {phase === "revealed" && (
        <motion.div className="BA__actions"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}>
          <button className="BA__action-btn" onClick={onClose}>← Back to Lobby</button>
          <button className="BA__action-btn BA__action-btn--share"
            onClick={() => {
              navigator.share?.({ title: "BESC Draw Battle", url: window.location.href }) ??
              navigator.clipboard.writeText(window.location.href);
              helperToast.success("Link copied!");
            }}>
            Share Result
          </button>
        </motion.div>
      )}

      {/* Timeout fallback */}
      {(phase === "cancelled" || phase === "timeout") && (
        <div className="BA__actions">
          <button className="BA__action-btn" onClick={onClose}>← Back to Lobby</button>
        </div>
      )}
    </motion.div>
  );
}
